/* NUMERO CON SOGLIA — Tenuta, Nei ribassi, A pari rischio.
 *
 * Regola 4 di STATO.md, la più facile da violare senza accorgersene:
 * ogni numero con soglia porta colore + verdetto + qualificatore di
 * affidabilità, TUTTI SEMPRE VISIBILI, mai in un suggerimento al passaggio del
 * mouse. Su telefono il passaggio del mouse non esiste, e il qualificatore è
 * proprio la riga che dice se del numero ci si può fidare.
 *
 * Il componente base non accetta un colore né un testo libero: accetta una
 * `LetturaSoglia` prodotta da `lib/metriche`. È l'unico modo di garantire che
 * i quattro pezzi arrivino insieme — con due props separate, prima o poi una
 * schermata ne passa tre su quattro.
 */

import { ScheletroLista, StatoErrore } from './Stati';
import {
  DRAWDOWN_OBIETTIVO,
  leggiAPariRischio,
  leggiIntorno,
  leggiNeiRibassi,
  leggiTenuta,
  type DivisionePeriodo,
  type LetturaSoglia,
} from '../lib/metriche';
import { drawdown as fmtDrawdown, pct } from '../lib/formato';
import type { StatoDati } from '../api/usaRichiesta';
import { Scheda } from './Base';

/**
 * Densità `scheda` (predefinita): il blocco con bordo, imbottitura e testata.
 * Densità `cella`: gli stessi quattro pezzi dentro una cella di tabella.
 *
 * La cella esiste perché il blocco, moltiplicato per quattro colonne e
 * venticinque righe, non ci sta — e la scorciatoia del prototipo, mostrare in
 * tabella solo valore e qualificatore, è proprio quella che la regola 4 non
 * concede: «24%» senza «il grosso era nel passato» accanto, in una riga
 * stretta letta di fretta, si legge come un buon numero.
 *
 * `titolo` e `periodo` sono obbligatori nella scheda e assenti nella cella:
 * in tabella il titolo è l'intestazione di colonna e il periodo è una colonna
 * a sé, quindi chiederli qui li farebbe ripetere su ogni riga — e una prop
 * obbligatoria che non finisce a schermo è un obbligo di firma che non diventa
 * un obbligo di schermo, cioè il difetto che si sta togliendo altrove.
 */
export interface PropsSogliaScheda {
  densita?: 'scheda';
  /** Il nome della metrica, es. "TENUTA". */
  titolo: string;
  lettura: LetturaSoglia;
  /** Il periodo su cui la metrica è misurata. Regola 2: mai senza. */
  periodo: string;
  stato?: StatoDati;
  onRiprova?: () => void;
}

/**
 * Densità `cella`: valore, verdetto e qualificatore in una cella di tabella.
 * Densità `cella-scorrevole`: gli stessi, MENO il qualificatore.
 *
 * La seconda è l'unica deroga alla regola 4, ed è una deroga chiesta: nella
 * lista di tutti i backtest Tenuta e Nei ribassi hanno il qualificatore sotto
 * («diviso per cicli di halving», «media su 4 anni negativi») e su cinquanta
 * righe sono cinquanta righe di testo in più — mentre lì si sta scorrendo per
 * scegliere quale riga aprire, non per giudicare un numero.
 *
 * Il qualificatore non sparisce dal dato: il tipo `LetturaSoglia` continua a
 * portarlo e il dettaglio del backtest lo mostra intero, che è il posto in cui
 * il numero si valuta davvero. Quello che resta anche qui è l'attenuazione del
 * numero fragile: se del numero non c'è da fidarsi, la lista lo mostra spento
 * comunque, altrimenti la deroga trasformerebbe un dato fragile in un dato
 * normale.
 *
 * È una densità a sé e non una prop booleana sulla `cella` perché così le altre
 * tabelle — i gruppi, il confronto — non possono perdere il qualificatore per
 * distrazione: devono chiedere questa densità per nome.
 */
export interface PropsSogliaCella {
  densita: 'cella';
  lettura: LetturaSoglia;
}

/* Due interfacce e non un discriminante a due valori: con `'cella' |
   'cella-scorrevole'` su una sola interfaccia TypeScript non riesce più a
   restringere il ramo `scheda`, e la garanzia che titolo e periodo siano
   obbligatori solo lì si perderebbe. */
export interface PropsSogliaCellaScorrevole {
  densita: 'cella-scorrevole';
  lettura: LetturaSoglia;
}

export type PropsNumeroConSoglia =
  | PropsSogliaScheda
  | PropsSogliaCella
  | PropsSogliaCellaScorrevole;

export function NumeroConSoglia(props: PropsNumeroConSoglia) {
  if (props.densita === 'cella' || props.densita === 'cella-scorrevole')
    return <CellaSoglia lettura={props.lettura} conQualificatore={props.densita === 'cella'} />;

  const { titolo, lettura, periodo, stato = 'pieno', onRiprova } = props;

  if (stato === 'caricamento') {
    return (
      <div className="sb-soglia">
        <div className="sb-etichetta">{titolo}</div>
        <div style={{ marginTop: 'var(--sb-s-5)' }}>
          <ScheletroLista righe={2} conIcona={false} />
        </div>
      </div>
    );
  }

  if (stato === 'errore') {
    return (
      <div className="sb-soglia sb-soglia--perdita">
        <StatoErrore titolo={`${titolo} non disponibile`} onRiprova={onRiprova} />
      </div>
    );
  }

  const fragile = lettura.qualificatore.fragile;
  const classi = [
    'sb-soglia',
    `sb-soglia--${lettura.tono}`,
    fragile ? 'sb-soglia--fragile' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classi}>
      <div className="sb-soglia__testata">
        <span className="sb-etichetta">{titolo}</span>
        <span className={`sb-soglia__banda sb-tono-${lettura.tono}`}>{lettura.banda}</span>
      </div>

      <div className={`sb-soglia__valore sb-tono-${lettura.tono}`}>{lettura.valore}</div>

      {/* Il verdetto ha lo stesso colore del numero: sono la stessa
          affermazione detta due volte, per chi non distingue i colori. */}
      <div className={`sb-soglia__verdetto sb-tono-${lettura.tono}`}>{lettura.verdetto}</div>

      <div className="sb-soglia__periodo">{periodo}</div>

      <div
        className={`sb-soglia__qualificatore${fragile ? ' sb-soglia__qualificatore--fragile' : ''}`}
      >
        {lettura.qualificatore.testo}
      </div>
    </div>
  );
}

/**
 * La lettura dentro una cella di tabella: colore, valore, verdetto e
 * qualificatore impilati e allineati a destra, senza contenitore.
 *
 * Non rende il `<td>`: lo mette il chiamante. Così la stessa forma vale sia
 * per una colonna sempre presente sia per una che compare solo se scelta.
 */
function CellaSoglia({
  lettura,
  conQualificatore,
}: {
  lettura: LetturaSoglia;
  conQualificatore: boolean;
}) {
  const fragile = lettura.qualificatore.fragile;
  return (
    <span className={`sb-cella-soglia${fragile ? ' sb-cella-soglia--fragile' : ''}`}>
      <span className={`sb-cella-soglia__valore sb-tono-${lettura.tono}`}>{lettura.valore}</span>
      {/* Il verdetto ha lo stesso colore del numero anche qui: la riga stretta
          è il posto in cui il numero da solo inganna di più. Resta anche nella
          densità scorrevole, perché è il pezzo che dice cosa SIGNIFICA il
          numero — togliere quello lascerebbe una percentuale nuda. */}
      <span className={`sb-cella-soglia__verdetto sb-tono-${lettura.tono}`}>{lettura.verdetto}</span>
      {conQualificatore && (
        <span
          className={`sb-cella-soglia__qualificatore${
            fragile ? ' sb-cella-soglia__qualificatore--fragile' : ''
          }`}
        >
          {lettura.qualificatore.testo}
        </span>
      )}
    </span>
  );
}

/* ==========================================================================
 * Le quattro metriche con soglia, già cablate sulla loro lettura.
 * ======================================================================== */

/**
 * TENUTA — rende ancora come rendeva.
 * `null` significa storia troppo corta e si mostra `n/d` in grigio: mai zero,
 * perché uno zero si legge come un giudizio invece che come un dato mancante.
 */
export function Tenuta({
  valore,
  divisione,
  periodo,
  stato,
  onRiprova,
}: {
  valore: number | null | undefined;
  /** `'ciclo'` del backend = cicli di halving; `'meta'` = a metà, meno affidabile. */
  divisione: DivisionePeriodo;
  periodo: string;
  stato?: StatoDati;
  onRiprova?: () => void;
}) {
  return (
    <NumeroConSoglia
      titolo="Tenuta"
      lettura={leggiTenuta(valore, divisione)}
      periodo={periodo}
      stato={stato}
      onRiprova={onRiprova}
    />
  );
}

/** Traduce `walkForwardMetodo` del backend nella divisione del periodo. */
export function divisioneDaMetodo(metodo: 'ciclo' | 'meta' | null | undefined): DivisionePeriodo {
  return metodo === 'ciclo' ? 'halving' : 'meta';
}

/**
 * NEI RIBASSI — cosa fa quando il mercato scende.
 * Sotto i due anni negativi il numero è fragile: si mostra attenuato e col
 * motivo scritto, non si nasconde. Nasconderlo lo farebbe cercare altrove.
 */
export function NeiRibassi({
  valore,
  anniNegativi,
  periodo,
  stato,
  onRiprova,
}: {
  valore: number | null | undefined;
  anniNegativi: number | null | undefined;
  periodo: string;
  stato?: StatoDati;
  onRiprova?: () => void;
}) {
  return (
    <NumeroConSoglia
      titolo="Nei ribassi"
      lettura={leggiNeiRibassi(valore, anniNegativi)}
      periodo={periodo}
      stato={stato}
      onRiprova={onRiprova}
    />
  );
}

/**
 * A PARI RISCHIO — il confronto che ribalta la classifica.
 * Il drawdown obiettivo è un parametro: cambiandolo cambia la classifica,
 * quindi va sempre scritto accanto al numero. È il qualificatore obbligatorio.
 */
export function APariRischio({
  profitto,
  drawdown,
  periodo,
  obiettivo = DRAWDOWN_OBIETTIVO,
  stato,
  onRiprova,
}: {
  profitto: number | null | undefined;
  drawdown: number | null | undefined;
  periodo: string;
  obiettivo?: number;
  stato?: StatoDati;
  onRiprova?: () => void;
}) {
  return (
    <NumeroConSoglia
      titolo="A pari rischio"
      lettura={leggiAPariRischio(profitto, drawdown, obiettivo)}
      periodo={periodo}
      stato={stato}
      onRiprova={onRiprova}
    />
  );
}

/**
 * INTORNO — altopiano o picco.
 * Cablato qui perché le tre schermate che lo mostrano scrivevano ognuna il
 * proprio titolo e la propria chiamata: bastava che una scrivesse "Vicinato"
 * o invertisse due argomenti perché la stessa colonna dicesse due cose.
 */
export function Intorno({
  valore,
  parametroPiuFragile,
  assi,
  periodo,
  stato,
  onRiprova,
}: {
  valore: number | null | undefined;
  parametroPiuFragile: string | null | undefined;
  assi: number | null | undefined;
  periodo: string;
  stato?: StatoDati;
  onRiprova?: () => void;
}) {
  return (
    <NumeroConSoglia
      titolo="Intorno"
      lettura={leggiIntorno(valore, parametroPiuFragile, assi)}
      periodo={periodo}
      stato={stato}
      onRiprova={onRiprova}
    />
  );
}

/**
 * Confronto nominale ↔ normalizzato, accostati.
 * Mai il nominale da solo: è quello che fa sembrare vincente la configurazione
 * che porta sette punti di drawdown in più.
 */
export function ConfrontoAPariRischio({
  profitto,
  drawdown,
  obiettivo = DRAWDOWN_OBIETTIVO,
  nota,
}: {
  profitto: number;
  drawdown: number;
  obiettivo?: number;
  nota?: string;
}) {
  const lettura = leggiAPariRischio(profitto, drawdown, obiettivo);
  return (
    <Scheda variante="incassata">
      <div className="sb-pari-rischio__riga">
        <span className="sb-pari-rischio__etichetta">Nominale</span>
        <span className="sb-pari-rischio__nominale sb-tono-guadagno">{pct(profitto)}</span>
        <span className="sb-numero sb-tono-perdita" style={{ fontSize: 'var(--sb-t-13)' }}>
          {fmtDrawdown(drawdown)}
        </span>
      </div>
      <hr className="sb-divisore" />
      <div className="sb-pari-rischio__riga">
        <span className="sb-pari-rischio__etichetta">A pari rischio</span>
        <span className={`sb-pari-rischio__normalizzato sb-tono-${lettura.tono}`}>
          {lettura.valore}
        </span>
      </div>
      <div className="sb-soglia__qualificatore">
        {nota ?? lettura.qualificatore.testo}
      </div>
    </Scheda>
  );
}
